import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import ResponsivePage from "../../common/core/ResponsivePage";
import { useEntities } from "@emberly/zenith-client";
import DashboardMapCard from "./cards/DashboardMapCard";
import NumberCard from "../../common/display/NumberCard";
import { ACTIVE_MISSIONS, ALL_OPEN_ORDERS } from "../../../common/queries";
import { Alert, AlertTitle, Button, Collapse, Grid } from "@mui/material";
import { Cached as CachedIcon } from "@mui/icons-material";
import { useDevice } from "../../../providers/DeviceProvider";
import config from "../../../config";
import { useSelector } from "react-redux";
import { AddMissionFab } from "../../../hooks/useAddMissionFab";
import { useEmployee } from "../../../providers/EmployeeProvider";
import { useStation } from "../../../providers/StationProvider";
import moment from "moment/moment";
import useReRender from "../../../hooks/useReRender";
import { NavLink } from "react-router-dom";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      background: "rgb(240, 242, 245)"
    },
    version: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3)
    },
    updatingIcon: {
      animationName: "rotate",
      animationDuration: "3s",
      animationIterationCount: "infinite",
      animationTimingFunction: "linear",
    },
    alertLink: {
      textDecoration: "none"
    }
  })
);

const nowrap = { whiteSpace: "nowrap" };

export default function HomePage() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { slim } = useDevice();
  const { entities: missions, loading: missionsLoading } = useEntities("Mission", ACTIVE_MISSIONS);
  const { entities: orders, loading: ordersLoading } = useEntities("Order", ALL_OPEN_ORDERS);
  const { serviceWorkerUpdated, serviceWorkerRegistration } = useSelector(state => state.app);
  const [updating, setUpdating] = useState(false);
  const reRender = useReRender(1000 * 60 * 5);

  const { isDispatch, openRequests, requestsLoading } = useEmployee();
  const { station } = useStation();

  const showMissionsLoading = missionsLoading && missions.length === 0;
  const showOrdersLoading = ordersLoading && orders.length === 0;

  const updateServiceWorker = useCallback(() => {
    if (!!serviceWorkerRegistration) {
      setUpdating(true);
      const registrationWaiting = serviceWorkerRegistration.waiting;
      if (registrationWaiting) {
        registrationWaiting.postMessage({ type: "SKIP_WAITING" });
        registrationWaiting.addEventListener("statechange", e => {
          if (e.target.state === "activated") {
            setTimeout(() => window.location.reload(), 500);
          }
        });
      }
    }
  }, [serviceWorkerRegistration]);

  const {
    activeServices,
    inStorage,
    delayedRequests
  } = useMemo(() => {
    return {
      activeServices: missions.reduce((acc, mission) => acc + mission.salvageTasks.filter(t => !t.execution?.history?.completed && !t.execution?.history?.acknowledged).length, 0),
      inStorage: missions.reduce((acc, mission) => acc + (!!mission.storageTask && !mission.storageTask.delivered ? 1 : 0), 0),
      delayedRequests: openRequests.filter(t => moment(t.created).isBefore(moment().subtract(1, "hour")))
    }
  }, [missions, openRequests, reRender]);

  return (
    <ResponsivePage
      title={t("home:title")}
      className={classes.root}
      maxWidth={config.pageSize.large}
    >
      <Grid item xs={12}>


        <Grid item container xs={12} justifyContent="stretch" spacing={slim ? 2 : 3}>
          {
            delayedRequests?.length > 0 && isDispatch ? (
              <Grid item xs={12}>
                <NavLink to="/network/requests/received" className={classes.alertLink}>
                  <Alert
                    sx={{ borderRadius: 2 }}
                    severity="error"
                    variant="filled"
                    action={
                      !slim ? (
                        <Button sx={{ color: "error.contrast" }}>{t("home:dispatchAlert:action")}</Button>
                      ) : null
                    }
                  >
                    <AlertTitle>{t("home:dispatchAlert:title")}</AlertTitle>
                    {t("home:dispatchAlert:description")}
                  </Alert>
                </NavLink>
              </Grid>
            ) : null
          }



          {
            isDispatch ? (
              <NumberCard
                title={t("home:cards:newRequests:title")}
                linkText={t("home:cards:newRequests:linkText")}
                to="/network/requests/received"
                loading={requestsLoading}
                color={openRequests?.length === 0 ? "success" : (delayedRequests.length > 0 ? "warning" : "textPrimary")}
                value={openRequests?.length || 0}
                xs={6} sm={6} md={6} lg={4}
              />
            ) : null
          }

          <NumberCard
            title={t("home:cards:openMissions:title")}
            linkText={t("home:cards:openMissions:linkText")}
            to="/activemissions"
            loading={showMissionsLoading}
            value={missions.length}
            xs={6} sm={6} md={6} lg={4}
          />

          <NumberCard
            title={t("home:cards:activeServices:title")}
            linkText={t("home:cards:activeServices:linkText")}
            to="/dispatch"
            loading={showMissionsLoading}
            value={activeServices}
            xs={6} sm={6} md={6} lg={4}
          />

          <NumberCard
            title={t("home:cards:inStorage:title")}
            linkText={t("home:cards:inStorage:linkText")}
            to="/storage"
            loading={showMissionsLoading}
            value={inStorage}
            xs={6} sm={6} md={6} lg={4}
          />

          <NumberCard
            title={t("home:cards:openOrders:title")}
            linkText={t("home:cards:openOrders:linkText")}
            to="/orders"
            loading={showOrdersLoading}
            value={orders.length}
            xs={6} sm={6} md={6} lg={4}
          />

          <NumberCard
            title={t("home:cards:dispatcherOnDuty:title")}
            linkText={t("home:cards:dispatcherOnDuty:linkText")}
            textVariant="h6"
            to="/operators"
            loading={!station}
            value={station?.dispatcherOnDuty?.name || "-"}
            xs={6} sm={6} md={6} lg={4}
          />

        </Grid>
      </Grid>

      <DashboardMapCard missions={missions} />

      <Grid item container justifyContent="center" xs={12}>
        <Button
          onClick={serviceWorkerUpdated ? updateServiceWorker : undefined}
          className={classes.version}
          disabled={!serviceWorkerUpdated || updating}
          endIcon={serviceWorkerUpdated ? <CachedIcon className={updating ? classes.updatingIcon : undefined} /> : undefined}
        >
          Assist - v{config.version} {serviceWorkerUpdated ? <Collapse in={!updating} orientation="horizontal" style={nowrap}>&nbsp;- {t("home:updateAvailable")}</Collapse> : null}
        </Button>
      </Grid>

      <AddMissionFab />

    </ResponsivePage>
  );
}
